/**
 * useDataEQConnector Hook
 *
 * React Query hook for managing DataEQ connector state.
 * Provides methods for fetching, creating, and updating the DataEQ connector.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataeqConnectorService } from '../services/dataeqConnectorService';
import { updateConnectorStatus } from '../utils/connectorStorage';
import type {
  ConnectorResponse,
  ConnectorCreateRequest,
  ConnectorUpdateRequest,
} from '../services/types';
import { APIErrorException } from '../services/types/errors';

// Query configuration constants
const MAX_RETRIES = 2; // Maximum number of retry attempts for failed queries
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes - time before data is considered stale

/**
 * Query key factory for DataEQ connector queries
 *
 * Provides consistent query keys for React Query caching and invalidation.
 * Use these keys to access or invalidate connector-related queries.
 *
 * @example
 * // Invalidate all connector queries
 * queryClient.invalidateQueries({ queryKey: dataeqConnectorKeys.all });
 * // Invalidate only DataEQ connector
 * queryClient.invalidateQueries({ queryKey: dataeqConnectorKeys.dataeq() });
 */
export const dataeqConnectorKeys = {
  all: ['connector'] as const,
  dataeq: () => ['connector', 'dataeq'] as const,
};

/**
 * Hook to fetch the current DataEQ connector configuration
 *
 * Returns connector status, organisation data from metadata service,
 * and associated dataset information. Automatically updates localStorage
 * with current status and implements retry logic for failed requests.
 *
 * @returns React Query result with connector data, loading state, and error handling
 * @example
 * const { data, isLoading, error } = useDataEQConnector();
 * if (data) {
 *   console.log(data.connector.name);
 * }
 */
export const useDataEQConnector = () => {
  return useQuery<ConnectorResponse, Error>({
    queryKey: dataeqConnectorKeys.dataeq(),
    queryFn: async () => {
      const response = await dataeqConnectorService.get();

      // Update localStorage with current status (from dataset, not connector)
      if (response.dataset?.status) {
        updateConnectorStatus(response.dataset.status as 'active' | 'inactive' | 'error');
      }

      return response;
    },
    retry: (failureCount, error) => {
      // Don't retry on 404 (connector not found) or 401 (unauthorized)
      if (error instanceof APIErrorException) {
        if (error.status === 404 || error.status === 401) {
          return false;
        }
      }
      // Retry up to MAX_RETRIES times for server errors
      return failureCount < MAX_RETRIES;
    },
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to create a new DataEQ connector
 *
 * Workflow:
 * 1. Generates Good Outcomes API key
 * 2. Creates organisation in metadata service
 * 3. Creates dataset in local database
 * 4. Updates query cache and localStorage
 *
 * @returns React Query mutation for creating connector
 * @example
 * const createConnector = useCreateDataEQConnector();
 * createConnector.mutate({
 *   connectorName: 'My Connector',
 *   dataeqAccounts: [{ code: 'ORG001', dataeqApiKey: 'key123' }]
 * });
 */
export const useCreateDataEQConnector = () => {
  const queryClient = useQueryClient();

  return useMutation<ConnectorResponse, APIErrorException, ConnectorCreateRequest>({
    mutationFn: async (request: ConnectorCreateRequest) => {
      return dataeqConnectorService.create(request);
    },
    onSuccess: async (data) => {
      // Update the cached connector data
      queryClient.setQueryData(dataeqConnectorKeys.dataeq(), data);

      // Update localStorage (status comes from dataset)
      if (data.dataset?.status) {
        updateConnectorStatus(data.dataset.status as 'active' | 'inactive' | 'error');
      }

      // Invalidate related queries
      await queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

/**
 * Hook to update an existing DataEQ connector
 *
 * Uses PATCH semantics to update connector name and DataEQ accounts.
 * Organisation code is immutable and cannot be changed after creation.
 * Automatically updates query cache and localStorage on success.
 *
 * @returns React Query mutation for updating connector
 * @example
 * const updateConnector = useUpdateDataEQConnector();
 * updateConnector.mutate({
 *   connectorName: 'Updated Name',
 *   addDataeqAccounts: [{ code: 'NEW001', dataeqApiKey: 'newkey' }],
 *   removeDataeqAccounts: ['OLD001']
 * });
 */
export const useUpdateDataEQConnector = () => {
  const queryClient = useQueryClient();

  return useMutation<ConnectorResponse, APIErrorException, ConnectorUpdateRequest>({
    mutationFn: async (request: ConnectorUpdateRequest) => {
      return dataeqConnectorService.update(request);
    },
    onSuccess: async (data) => {
      // Update the cached connector data
      queryClient.setQueryData(dataeqConnectorKeys.dataeq(), data);

      // Update localStorage (status comes from dataset)
      if (data.dataset?.status) {
        updateConnectorStatus(data.dataset.status as 'active' | 'inactive' | 'error');
      }

      // Invalidate datasets in case accounts changed
      await queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

/**
 * Hook to set DataEQ connector active status (enable/disable)
 *
 * When deactivated (active=false), soft-deletes the organisation in metadata service.
 * When activated (active=true), restores the organisation. Automatically updates
 * localStorage and invalidates related queries to refetch updated data.
 *
 * @returns React Query mutation for activating or deactivating connector
 * @example
 * const setActive = useSetDataEQConnectorActive();
 * // To activate
 * setActive.mutate(true);
 * // To deactivate
 * setActive.mutate(false);
 */
export const useSetDataEQConnectorActive = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string; status: string },
    APIErrorException,
    boolean
  >({
    mutationFn: async (active: boolean) => {
      return dataeqConnectorService.setActive(active);
    },
    onSuccess: async (data) => {
      // Update localStorage with new status (cast to ConnectorStatus since backend returns 'active' or 'inactive')
      updateConnectorStatus(data.status as 'active' | 'inactive');

      // Invalidate connector query to refetch updated status
      await queryClient.invalidateQueries({ queryKey: dataeqConnectorKeys.dataeq() });

      // Invalidate datasets as status change might affect data flow
      await queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};
